<script setup lang="ts">
import { VDataTableVirtual } from 'vuetify/labs/VDataTable'
import { $getLineItemHistory } from '@/api/brandings'
import { $getSignHistory } from '@/api/signs'
import { $getKopItemHistory } from '@/api/kop'

interface IProps {
  modelValue?: boolean,
  itemId?: number | null,
  itemType?: 'branding' | 'wayfinding' | 'kop',
  code?: string | null,
  isLineItem?: boolean,
}

const props = withDefaults(defineProps<IProps>(), {
  modelValue: false,
  itemId: null,
  itemType: 'branding',
  code: null,
  isLineItem: false,
})

interface CustomEvents {
  (e: 'update:modelValue', value: boolean): void
  (e: 'onClose'): void
}

const emit = defineEmits<CustomEvents>()

const historyRecords = ref([])
const historyRecordsLoading = ref(false)

const tableHeaders = [
  { key: 'item_status_name', title: 'Status', sortable: false },
  { key: 'sign_status_name', title: 'Status', sortable: false },
  { key: 'change_reason', title: 'Activity', sortable: false },
  { key: 'user_name', title: 'User Name', sortable: false },
  { key: 'change_date', title: 'Date/Time', sortable: false },
  { key: 'next_status', title: 'Next Status', sortable: false },
]

const computedTableHeaders = computed(() => {
  if (props.itemType === 'wayfinding') {
    return tableHeaders.filter(i => i.key !== 'item_status_name')
  }
  return tableHeaders.filter(i => i.key !== 'sign_status_name')
})

const getHistoryRecords = async () => {
  historyRecordsLoading.value = true
  const filters = {
    id: props.itemId,
  }
  try {
    let getItemHistoryRequest = null
    switch (props.itemType) {
      case 'branding':
        getItemHistoryRequest = $getLineItemHistory
        break
      case 'wayfinding':
        getItemHistoryRequest = $getSignHistory
        break
      case 'kop':
        getItemHistoryRequest = $getKopItemHistory
        break
      default:
        break
    }
    if (!getItemHistoryRequest) return

    const res = await getItemHistoryRequest(filters)
    historyRecords.value = res.data
  } catch (e) {
    console.log(e)
  } finally {
    historyRecordsLoading.value = false
  }
}

const closeDialog = () => {
  emit('update:modelValue', !props.modelValue)
}

const onClose = async () => {
  emit('onClose')
}

watch(() => props.modelValue, () => {
  if (props.modelValue) {
    getHistoryRecords()
  }
})

const prName = computed(() => {
  if (props.itemType === 'wayfinding') {
    if (props.isLineItem) {
      return 'Wayfinding Line Item'
    }
    return 'Wayfinding Sign'
  } else if (props.itemType === 'kop') {
    if (props.isLineItem) {
      return 'KOP Line Item'
    }
    return 'KOP Item'
  }
  return 'Line Item'
})

</script>

<template>
  <VDialog
    :model-value="modelValue"
    max-width="1200"
    class="default-dialog"
    @after-leave="onClose"
    @update:modelValue="emit('update:modelValue')"
  >
    <VBtn
      variant="elevated"
      density="compact"
      icon="tabler-x"
      class="v-dialog-close-btn"
      @click="closeDialog"
    />

    <VCard>
      <VCardTitle class="vss-movable">
        <MovableDialog />

        Status History of {{ prName }} {{ props?.code ? props.code : '' }}
      </VCardTitle>
      <VCardText>
        <VRow>
          <VCol
            cols="12"
            class="py-0 mt-1"
          >
            <VDataTableVirtual
              :headers="computedTableHeaders"
              :items="historyRecords"
              :loading="historyRecordsLoading"
              no-data-text="History records not found"
              hover
              density="compact"
            />
          </VCol>
        </VRow>
      </VCardText>
    </VCard>
  </VDialog>
</template>
